import { useEffect, useRef, useState, type RefObject } from 'react';
import { getScrollContainer, prefersReducedMotion, subscribeScroll, type ScrollState } from './scroll-engine';

/** Clamp a number into the [min, max] range. */
export function clamp(value: number, min = 0, max = 1): number {
    return value < min ? min : value > max ? max : value;
}

/** Linear interpolation between `a` and `b` by `t` (0..1). */
export function lerp(a: number, b: number, t: number): number {
    return a + (b - a) * t;
}

/** The "premium" easing the spec asks for: cubic-bezier(0.22, 1, 0.36, 1) ≈ easeOutExpo-ish. */
export function easeOutCubic(t: number): number {
    return 1 - Math.pow(1 - t, 3);
}

/**
 * A tiny observable scalar — a stripped-down MotionValue. Scroll-driven primitives
 * write to it each frame and bound DOM nodes read from it imperatively, so motion
 * never round-trips through React state (no per-frame re-renders).
 */
export class MotionValue {
    private value: number;
    private readonly subs = new Set<(v: number) => void>();

    constructor(initial = 0) {
        this.value = initial;
    }

    get(): number {
        return this.value;
    }

    set(next: number): void {
        if (next === this.value) return;
        this.value = next;
        this.subs.forEach((cb) => cb(next));
    }

    /** Subscribe to changes. Fires immediately with the current value. Returns unsubscribe. */
    subscribe(cb: (v: number) => void): () => void {
        this.subs.add(cb);
        cb(this.value);
        return () => {
            this.subs.delete(cb);
        };
    }
}

/** Stable MotionValue for the lifetime of the component. */
export function useMotionValue(initial = 0): MotionValue {
    const ref = useRef<MotionValue | undefined>(undefined);
    return (ref.current ??= new MotionValue(initial));
}

/**
 * Bind a MotionValue to a DOM node: `apply` runs imperatively whenever the value
 * changes (and once on mount). Use this to set transforms/opacity without
 * re-rendering React.
 */
export function useMotion(
    ref: RefObject<HTMLElement | null>,
    value: MotionValue,
    apply: (el: HTMLElement, v: number) => void,
): void {
    const applyRef = useRef(apply);
    applyRef.current = apply;
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        return value.subscribe((v) => applyRef.current(el, v));
    }, [ref, value]);
}

/**
 * Scene progress for a tall, sticky-pinned section: 0 when the section's top
 * reaches the top of the viewport, 1 when its bottom reaches the bottom. Drives
 * scrubbed timelines (horizontal panel tracks, card swaps, draws).
 */
export function useScrollScene(ref: RefObject<HTMLElement | null>): MotionValue {
    const value = useMotionValue(0);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const compute = (state: ScrollState): void => {
            const rect = el.getBoundingClientRect();
            const distance = rect.height - state.viewportHeight;
            value.set(distance > 0 ? clamp(-rect.top / distance) : 0);
        };
        return subscribeScroll(compute);
    }, [ref, value]);
    return value;
}

/**
 * Viewport progress for a normal in-flow element: 0 as it enters from the bottom,
 * 0.5 when centered, 1 as it exits the top. Useful for parallax and scrubbed
 * decorative motion that should track an element as it passes through the screen.
 */
export function useViewportProgress(ref: RefObject<HTMLElement | null>): MotionValue {
    const value = useMotionValue(0);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const compute = (state: ScrollState): void => {
            const rect = el.getBoundingClientRect();
            const span = state.viewportHeight + rect.height;
            value.set(clamp((state.viewportHeight - rect.top) / span));
        };
        return subscribeScroll(compute);
    }, [ref, value]);
    return value;
}

/**
 * Active step index (0..steps-1) for a tall pinned section, derived from scene
 * progress. Drives scroll-controlled card swaps: each step owns an equal slice of
 * the runway. Only re-renders when the index actually changes.
 */
export function useScrollStep(ref: RefObject<HTMLElement | null>, steps: number): number {
    const [index, setIndex] = useState(0);
    const current = useRef(0);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const compute = (state: ScrollState): void => {
            const rect = el.getBoundingClientRect();
            const distance = rect.height - state.viewportHeight;
            const p = distance > 0 ? clamp(-rect.top / distance) : 0;
            const idx = Math.min(steps - 1, Math.max(0, Math.floor(p * steps)));
            if (idx !== current.current) {
                current.current = idx;
                setIndex(idx);
            }
        };
        return subscribeScroll(compute);
    }, [ref, steps]);
    return index;
}

/**
 * Boolean "has this element entered the viewport" — for one-shot entrance reveals
 * driven by a class toggle (e.g. the metrics knob slide). Observes against the
 * page scroll container so it works inside `.page-scroll-root`.
 */
export function useInView(
    ref: RefObject<HTMLElement | null>,
    { threshold = 0.3, once = true }: { threshold?: number; once?: boolean } = {},
): boolean {
    const [inView, setInView] = useState(false);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const io = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting) {
                    setInView(true);
                    if (once) io.disconnect();
                } else if (!once) {
                    setInView(false);
                }
            },
            { threshold, root: getScrollContainer() },
        );
        io.observe(el);
        return () => io.disconnect();
    }, [ref, threshold, once]);
    return inView;
}

export { prefersReducedMotion };
